import type React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Close } from "@mui/icons-material";
import { Box, Divider, Fade, IconButton } from "~/common/mui";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import type { TreeNode } from "~/domains/navigation/types/NavigationTypes";
import { MobileNavButton } from "~/features/categoryNavigation/components/MobileNavButton";
import { LanguageSelector } from "~/features/categoryNavigation/components/LanguageSelector";
import { NavigationTree } from "~/features/categoryNavigation/components/shared";
import MenuIcon from "@mui/icons-material/Menu";
import {
  BackButton,
  DrawerFooter,
  DrawerHeader,
  NavigationDrawer,
} from "~/features/categoryNavigation/components/CategoryNavigation.styles";

interface MobileNavigationProps {
  tree: TreeNode;
  nodesInTransition: TreeNode[];
  isOpen: boolean;
  delay: number;
  offsetStyles: React.CSSProperties;
  selectNode: (node: TreeNode, noChildren?: boolean) => void;
  onMouseEnter: (node: TreeNode, noChildren?: boolean) => void;
  onMouseLeave: (node: TreeNode) => void;
  close: () => void;
  goBack: () => void;
  toggleOpen: () => void;
}

export const MobileNavigation = ({
  tree,
  nodesInTransition,
  isOpen,
  delay,
  offsetStyles,
  selectNode,
  onMouseEnter,
  onMouseLeave,
  close,
  goBack,
  toggleOpen,
}: MobileNavigationProps) => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();

  const anchor: "left" | "bottom" = screenSize === useNumericScreenSize.tablet ? "left" : "bottom";

  const visibleNodes = nodesInTransition.slice(-1);

  return (
    <>
      <MobileNavButton onClick={toggleOpen} icon={isOpen ? <Close /> : <MenuIcon />} outlinedIcon={!isOpen} />
      <NavigationDrawer
        open={isOpen}
        anchor={anchor}
        onClose={close}
        style={offsetStyles}
        ModalProps={{
          keepMounted: true, // content stays mounted even when closed
        }}
      >
        <DrawerHeader>
          <Fade in={nodesInTransition.length > 1} timeout={delay}>
            <BackButton onClick={goBack} startIcon={<ChevronLeft />} variant={"text"}>
              {t("generic.back")}
            </BackButton>
          </Fade>
          <IconButton size="small" variant={"contained"} color={"secondary"} onClick={close}>
            <Close />
          </IconButton>
        </DrawerHeader>

        <Box sx={{ display: "flex", flexDirection: "row", padding: 2 }}>
          <NavigationTree
            tree={tree}
            visibleNodes={visibleNodes}
            onItemClick={selectNode}
            onItemHover={onMouseEnter}
            onItemLeave={onMouseLeave}
          />
        </Box>

        <DrawerFooter>
          <Divider sx={{ marginTop: "8px", marginBottom: "16px" }} />
          <LanguageSelector />
        </DrawerFooter>
      </NavigationDrawer>
    </>
  );
};
